import { DataTable } from "@/components/molecules/tables/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { brandDocFor } from "@/features/devices/brand-docs";
import {
  useDeleteDeviceBase,
  useDeviceBasePrompt,
  useDeviceBases,
} from "@/features/devices/hooks/use-devices";
import type { DeviceBase } from "@/features/devices/types";
import { useApiScope, useClientId } from "@/features/utils/api-scope";
import { useDateFnsLocale, useTranslation } from "@/features/utils/i18n";
import { DotsThree, Info, PencilSimple, Plus, Trash } from "@phosphor-icons/react";
import type { ColumnDef } from "@tanstack/react-table";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

const BaseAction = ({ base }: { base: DeviceBase }) => {
  const { t } = useTranslation("devices");
  const clientId = useClientId();
  const { show } = useDeviceBasePrompt();
  const remove = useDeleteDeviceBase();

  const handleDelete = async () => {
    if (!clientId) return;
    try {
      await remove.mutateAsync({ clientId, baseId: base.id! });
      toast.success(t("devices.bases.deleted"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("devices.bases.deleteFailed"),
      );
    }
  };

  return (
    <div className="text-right">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <DotsThree className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => show(base.id!)}>
            <PencilSimple className="mr-2 h-4 w-4" />
            {t("devices.bases.actions.open")}
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/*
            No confirmation step, unlike re-issuing SIP credentials: this
            deletes written-down codes, it does not take a phone off the line —
            the handsets are detached and keep registering. The audit trail
            keeps the DELETE snapshot, so the codes are recoverable by staff.
          */}
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={handleDelete}
          >
            <Trash className="mr-2 h-4 w-4" />
            {t("devices.bases.actions.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

/**
 * What is filled in and what is missing, at a glance.
 *
 * A base whose PIN nobody wrote down is the failure this table exists to
 * prevent, and it is invisible in a row that just shows a label — so the
 * missing pieces are named rather than left as empty cells somebody has to
 * notice.
 */
const Completeness = ({ base }: { base: DeviceBase }) => {
  const { t } = useTranslation("devices");

  const missing = [
    !base.base_pin && "base_pin",
    !base.web_password && "web_password",
  ].filter(Boolean) as string[];

  if (missing.length === 0)
    return (
      <Badge className="bg-emerald-600 hover:bg-emerald-600">
        {t("devices.bases.complete")}
      </Badge>
    );

  return (
    <div className="space-y-0.5">
      {missing.map((field) => (
        <Badge key={field} variant="outline">
          {t("devices.bases.missing", {
            field: t(`devices.bases.fields.${field}`),
          })}
        </Badge>
      ))}
    </div>
  );
};

const useBaseColumns = (): ColumnDef<DeviceBase>[] => {
  const { t } = useTranslation("devices");
  const locale = useDateFnsLocale();
  const staff = useApiScope() === "staff";

  return [
    {
      accessorKey: "label",
      header: t("devices.bases.columns.label"),
      cell: ({ row }) => (
        <div className="space-y-0.5">
          <div className="font-medium">{row.original.label}</div>
          {row.original.brand && (
            <div className="text-muted-foreground flex items-center gap-1 text-xs">
              {row.original.brand}
              {/*
                We have field notes for this make — the defaults, where the box
                shows its IP. Marked here so somebody about to go on site knows
                to open the row.
              */}
              {brandDocFor(row.original.brand) && (
                <Info className="h-3 w-3" aria-hidden />
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      id: "devices",
      header: t("devices.bases.columns.devices"),
      cell: ({ row }) =>
        row.original.devices.length === 0 ? (
          <span className="text-muted-foreground text-sm">
            {t("devices.bases.columns.noDevices")}
          </span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {row.original.devices.map((device) => (
              <Badge key={device.id} variant="secondary">
                {device.label}
              </Badge>
            ))}
          </div>
        ),
    },
    {
      id: "completeness",
      header: t("devices.bases.columns.codes"),
      cell: ({ row }) => <Completeness base={row.original} />,
    },
    // The notes are the one free-text field and often carry where the box is
    // plugged in, which is what someone sent on site is looking for.
    {
      id: "notes",
      header: t("devices.bases.columns.notes"),
      cell: ({ row }) => (
        <span className="text-muted-foreground line-clamp-2 max-w-[18rem] text-xs">
          {row.original.notes || "—"}
        </span>
      ),
    },
    ...(staff
      ? ([
          {
            id: "updated",
            header: t("devices.bases.columns.updated"),
            cell: ({ row }) =>
              row.original.updated_at ? (
                <span className="text-muted-foreground text-xs">
                  {formatDistanceToNow(new Date(row.original.updated_at), {
                    addSuffix: true,
                    locale,
                  })}
                </span>
              ) : (
                <span className="text-muted-foreground text-xs">—</span>
              ),
          },
        ] as ColumnDef<DeviceBase>[])
      : []),
    {
      id: "actions",
      header: () => (
        <div className="text-right">{t("devices.bases.columns.actions")}</div>
      ),
      cell: ({ row }) => <BaseAction base={row.original} />,
    },
  ];
};

/**
 * The bases of a client — the physical hardware, as opposed to the SIP accounts
 * that run on it.
 *
 * Its own table rather than a column of the handset list, because a base is not
 * a property of a handset: several handsets share one, which is the whole
 * reason it became an object. The dialog is mounted here, once, for the same
 * reason it is on the handsets page — a dialog owned by a table cell is
 * dismissed mid-edit when a refetch reconciles the row.
 */
export const DeviceBasesTable = () => {
  const { t } = useTranslation("devices");
  const clientId = useClientId();
  const { data: bases = [], isLoading } = useDeviceBases(clientId || "");
  const { showNew } = useDeviceBasePrompt();
  const columns = useBaseColumns();

  return (
    <section className="space-y-3">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">{t("devices.bases.section")}</h3>
          <p className="text-muted-foreground text-sm">
            {t("devices.bases.sectionDescription")}
          </p>
        </div>
        <Button onClick={showNew}>
          <Plus className="mr-2 h-4 w-4" />
          {t("devices.bases.actions.create")}
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={bases}
        isLoading={isLoading}
        hideSearch
      />
    </section>
  );
};
